import { useI18n } from '@affine/i18n';
import { createEmptyViewConfigRow } from '@streamlain/view-config';
import { Framework, useLiveData, useService } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import { type FC, useCallback, useMemo } from 'react';

import { DocService, DocsService } from '../../../doc';
import { ComponentRegistry } from '../../services/component-registry';
import type { FullscreenViewProps } from '../../services/fullscreen-registry';
import { ViewConfigRowStore } from '../../stores/view-config-row.store';
import { ViewConfigRowForm } from './row-form';
import * as css from './shell.css';
import { deriveActiveCapabilities } from './utils/derive-active-capabilities';
import { useContentDbColumns } from './utils/get-content-db-columns';

export const ViewConfigFullscreenShell: FC<FullscreenViewProps> = ({
  blockId,
  onClose,
}) => {
  const t = useI18n();
  const docService = useService(DocService);
  const docsService = useService(DocsService);
  const registry = useService(ComponentRegistry);

  const rowStore = useMemo(() => {
    const fw = new Framework();
    fw.store(
      ViewConfigRowStore,
      () => new ViewConfigRowStore(docService, blockId)
    );
    return fw.provider().get(ViewConfigRowStore);
  }, [docService, blockId]);

  const rows = useLiveData(rowStore.rows$);
  const isEmpty = rows.length === 0;
  const activeRow = rows.find(r => r.isDefault) ?? rows[0];
  const columns = useContentDbColumns(activeRow, docService, docsService);
  const activeCapabilities = useMemo(
    () => (activeRow ? deriveActiveCapabilities(activeRow, registry) : []),
    [activeRow, registry]
  );

  const handleCreate = useCallback(() => {
    rowStore.createRow(
      createEmptyViewConfigRow({
        name: `View ${nanoid(6)}`,
        contentDbRef: null,
      })
    );
  }, [rowStore]);

  return (
    <div
      className={css.container}
      data-testid="view-config-editor"
      data-sl-state={isEmpty ? 'empty' : 'filled'}
    >
      <header className={css.topBar}>
        <h1 className={css.title}>
          {t['com.streamlain.customApps.viewConfig.title']()}
        </h1>
        <button
          type="button"
          className={css.closeButton}
          onClick={onClose}
          aria-label={t['com.streamlain.customApps.viewConfig.close']()}
        >
          {t['com.streamlain.customApps.viewConfig.close']()}
        </button>
      </header>
      <div className={css.content}>
        {isEmpty ? (
          <div className={css.emptyState}>
            <p>{t['com.streamlain.customApps.viewConfig.empty']()}</p>
            <button
              type="button"
              className={css.createButton}
              onClick={handleCreate}
            >
              {t['com.streamlain.customApps.viewConfig.createView']()}
            </button>
          </div>
        ) : activeRow ? (
          <ViewConfigRowForm
            row={activeRow}
            rowStore={rowStore}
            columns={columns}
            activeCapabilities={activeCapabilities}
          />
        ) : null}
      </div>
    </div>
  );
};
